import { Link } from 'react-router-dom';
import { Lock, Crown, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { sanitizeHtml } from '@/lib/sanitize';

/**
 * Overlay for locked cards (stories, articles, confessions, channels).
 * Renders an absolute-positioned darkening layer with a premium badge.
 */
export function PremiumCardOverlay({ className = '' }) {
  return (
    <div
      className={`absolute inset-0 z-10 flex items-center justify-center bg-black/70 backdrop-blur-[2px] ${className}`}
      data-testid="premium-card-overlay"
    >
      <div className="flex flex-col items-center gap-2 px-3 text-center">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gold/20 border border-gold/50">
          <Lock className="w-5 h-5 text-gold" />
        </div>
        <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">
          Conteúdo Premium
        </span>
      </div>
    </div>
  );
}

/**
 * Full-screen (in-page) block for detail views when content is locked.
 * Displays a premium CTA linking to /premium.
 */
export function PremiumGate({ title = 'Conteúdo Premium', message }) {
  const defaultMessage =
    'Este conteúdo está reservado a assinantes Premium. Faz já o upgrade para ter acesso ilimitado a todos os contos, artigos, confissões e canais.';
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-surface via-surface to-black p-8 md:p-12 text-center"
      data-testid="premium-gate"
    >
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-ruby/10 blur-3xl" />

      <div className="relative">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gold/30 to-ruby/30 border border-gold/50 mb-6">
          <Crown className="w-8 h-8 text-gold" />
        </div>

        <h2 className="font-brand text-2xl md:text-3xl font-bold text-foreground mb-3">
          {title}
        </h2>
        <p className="text-muted-foreground max-w-lg mx-auto mb-8">
          {message || defaultMessage}
        </p>

        <Link to="/premium">
          <Button
            data-testid="premium-gate-cta"
            className="bg-gradient-to-r from-ruby to-ruby-light hover:from-ruby-light hover:to-ruby text-white rounded-full px-8 py-6 font-semibold shadow-lg shadow-ruby/30"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Assinar Premium
          </Button>
        </Link>

        <p className="text-xs text-muted-foreground mt-4">
          Acesso ilimitado · Sem anúncios · Cancela quando quiser
        </p>
      </div>
    </div>
  );
}

/**
 * Netflix-style teaser: renders the first paragraphs of (HTML) content behind a
 * gradient fade, then stacks the PremiumGate directly below as a CTA.
 * `teaserHtml` is trusted HTML already prepared by the backend (truncated).
 */
export function PremiumTeaser({ teaserHtml, title, message, children }) {
  return (
    <div data-testid="premium-teaser">
      {teaserHtml ? (
        <div className="relative mb-6">
          <div
            className="reader-content text-muted-foreground"
            style={{ lineHeight: '1.9', textAlign: 'justify' }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(teaserHtml) }}
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background via-background/90 to-transparent" />
        </div>
      ) : children}
      <PremiumGate title={title} message={message} />
    </div>
  );
}
